import { useLayoutEffect, useRef } from "react";

// A LIFO stack of the Escape handlers for every currently-mounted dialog that
// uses useEscapeKey. A single document-level listener routes each Escape to the
// TOP of the stack only — the most recently mounted (visually topmost) dialog —
// and stops the event there. So one Escape dismisses only the topmost dialog,
// and a second Escape dismisses the next one down, even when neither dialog
// knows the other exists (e.g. a tag dialog opened over Settings). Without this,
// every dialog's own Escape listener fires on the same keypress and they all
// close at once.
interface EscapeEntry {
  handle: () => void;
}

const escapeStack: EscapeEntry[] = [];

function dispatchEscape(e: KeyboardEvent): void {
  if (e.key !== "Escape") return;
  const top = escapeStack[escapeStack.length - 1];
  if (!top) return;
  // Claim this Escape for the topmost dialog: stop it reaching the listeners
  // underneath (other dialogs, the global shortcut handler) so they don't also
  // act on it, then dismiss only the topmost dialog.
  e.preventDefault();
  e.stopImmediatePropagation();
  e.stopPropagation();
  top.handle();
}

function pushEscapeEntry(entry: EscapeEntry): void {
  if (escapeStack.length === 0) {
    // Capture phase so we run before the bubble-phase listeners underneath
    // (other dialogs, useKeyboardShortcuts) get a chance to act on the Escape.
    document.addEventListener("keydown", dispatchEscape, true);
  }
  escapeStack.push(entry);
}

function removeEscapeEntry(entry: EscapeEntry): void {
  const i = escapeStack.lastIndexOf(entry);
  if (i !== -1) escapeStack.splice(i, 1);
  if (escapeStack.length === 0) {
    document.removeEventListener("keydown", dispatchEscape, true);
  }
}

interface UseEscapeKeyOptions {
  /**
   * When false the dialog isn't placed on the Escape stack, so Escape neither
   * dismisses it nor is claimed from dialogs underneath (e.g. while a save is in
   * flight and Escape should be ignored). Default true.
   */
  enabled?: boolean;
}

/**
 * Runs `onEscape` when Escape is pressed — but only while this is the topmost
 * dialog using the hook (see the Escape-stack note above), so layered dialogs
 * close one at a time from the top down.
 *
 * The stack entry is registered in a layout effect (synchronously after the DOM
 * commit, before paint) rather than a passive effect: a passive effect runs
 * after paint, leaving a one-frame gap where a dialog is on screen but its
 * Escape handler isn't wired yet, so an Escape pressed the instant a dialog
 * opens is dropped. Wiring before paint closes that window. `onEscape` is held
 * in a ref so it always sees fresh state without re-registering.
 */
export function useEscapeKey(
  onEscape: () => void,
  { enabled = true }: UseEscapeKeyOptions = {},
): void {
  const onEscapeRef = useRef(onEscape);
  useLayoutEffect(() => {
    onEscapeRef.current = onEscape;
  });

  useLayoutEffect(() => {
    if (!enabled) return;
    const entry: EscapeEntry = { handle: () => onEscapeRef.current() };
    pushEscapeEntry(entry);
    return () => removeEscapeEntry(entry);
  }, [enabled]);
}
